import type * as React from 'react';

/**
 * One suggestion in the Composer mention typeahead. The consumer supplies these
 * (already filtered + sorted) for the current query; the Composer renders + inserts.
 */
export interface ComposerMentionItem {
  /** Stable id — the React key, and passed back to `onSelect`. */
  id: string;
  /**
   * Shown in the row AND inserted into the text as `${trigger}${label} `.
   *
   * Must be a run of word characters (`[\p{L}\p{N}_]+`) — letters, digits, or
   * underscore, no spaces or punctuation. Mentions serialise into the plain-text
   * `value` as `${trigger}${label}` and are re-detected from it on any rebuild;
   * a label with a space (`"Product Owner"`) would round-trip back to plain text
   * and lose its chip. Use a single-token label.
   */
  label: string;
  /** Optional leading node (e.g. an avatar dot / icon). */
  icon?: React.ReactNode;
}

/**
 * Mention typeahead configuration for {@link Composer}. Turns any Composer into a
 * `@mention`-style input: the Composer owns the trigger detection, keyboard
 * model, positioning, a11y, and text insertion; the consumer owns the data
 * (which items match a query) and any side effect on select (e.g. addressing a
 * message to that entity).
 */
export interface ComposerMentions {
  /** Character that opens the typeahead. Single char; defaults to `'@'`. */
  trigger?: string;
  /**
   * Items to show for the current query (the text typed after the trigger, up
   * to the caret). Empty query means "show all". The consumer filters + sorts;
   * returning `[]` closes the list.
   */
  getItems: (query: string) => readonly ComposerMentionItem[];
  /** Side effect when an item is committed, after the text has been inserted. */
  onSelect?: (item: ComposerMentionItem) => void;
  /** Accessible label for the suggestion listbox. Defaults to `'Suggestions'`. */
  listLabel?: string;
}

/** A run of composer text, flagged as a committed mention token or plain text. */
export interface MentionSegment {
  text: string;
  mention: boolean;
}

/**
 * Split `text` into plain + mention runs, so the editor can build committed
 * mentions (`@Label`) as atomic chip nodes and plain text as text nodes. A run
 * `${trigger}word` is a mention iff it is word-initial (start / after whitespace)
 * AND `isMention(word)` — i.e. the word matches a known item, so a half-typed or
 * bogus `@foo` is left as plain text. Pure + synchronous.
 */
export function segmentMentions(
  text: string,
  isMention: (word: string) => boolean,
  trigger = '@',
): MentionSegment[] {
  const segments: MentionSegment[] = [];
  // Word-initial `@` + a run of letters/digits/underscore (agent labels).
  const re = new RegExp(`(^|\\s)(${escapeRegExp(trigger)}[\\p{L}\\p{N}_]+)`, 'gu');
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const at = m.index + m[1]!.length; // index of the trigger char
    const token = m[2]!; // e.g. "@Architect"
    if (isMention(token.slice(1))) {
      if (at > last) segments.push({ text: text.slice(last, at), mention: false });
      segments.push({ text: token, mention: true });
      last = at + token.length;
    }
  }
  if (last < text.length) segments.push({ text: text.slice(last), mention: false });
  return segments;
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
